import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';


const OpportunityCard = ({ opportunity, onSwipe, isAnalyzing, onBlockCompany }) => {
  const {
    id,
    companyDescription,
    roleTitle,
    compensationRange,
    location,
    workMode,
    teamSize,
    growthStage,
    employeeCount,
    benefits,
    requirements,
    isExceptional,
    companyImage,
    companyImageAlt,
    matchScore,
    matchAnalysis
  } = opportunity;

  const getMatchColor = (score) => {
    if (!score) return 'text-[#6B7280]';
    if (score >= 80) return 'text-[#10B981]';
    if (score >= 60) return 'text-[#F59E0B]';
    return 'text-[#EF4444]';
  };

  const getMatchBgColor = (score) => {
    if (!score) return 'bg-[#E5E7EB]';
    if (score >= 80) return 'bg-[#10B981]/10';
    if (score >= 60) return 'bg-[#F59E0B]/10';
    return 'bg-[#EF4444]/10';
  };

  const getMatchLabel = (score) => {
    if (!score) return 'Analyzing...';
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    return 'Potential Match';
  };

  const handleBlockCompany = () => {
    if (onBlockCompany) {
      onBlockCompany(opportunity);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* AI Match Score Badge */}
      {(matchScore !== null && matchScore !== undefined) && (
        <div className={`${getMatchBgColor(matchScore)} px-4 py-3 border-b border-gray-100`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Icon name="Sparkles" size={16} color={matchScore >= 80 ? '#10B981' : matchScore >= 60 ? '#F59E0B' : '#EF4444'} />
                <span className={`font-semibold text-sm ${getMatchColor(matchScore)}`}>
                  {matchScore}% Match
                </span>
              </div>
              <span className="text-xs text-gray-600">{getMatchLabel(matchScore)}</span>
            </div>
            <div className="flex items-center gap-2">
              {opportunity?.isExceptional && (
                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-md text-xs font-semibold">
                  Exceptional
                </span>
              )}
              <button
                onClick={handleBlockCompany}
                className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md transition-colors"
                title="Block this company"
              >
                <Icon name="Ban" size={14} color="#6B7280" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {isAnalyzing && !matchScore && (
        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
          <div className="flex items-center gap-2">
            <Icon name="Loader" size={16} color="#3B82F6" className="animate-spin" />
            <span className="text-sm text-blue-700">Calculating your compatibility...</span>
          </div>
        </div>
      )}
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden">
        <Image
          src={companyImage}
          alt={companyImageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 right-4 flex gap-2">
          {isExceptional && (
            <div className="bg-accent text-accent-foreground px-4 py-2 rounded-lg border border-accent/20 text-sm font-medium flex items-center gap-2">
              <Icon name="Award" size={16} />
              <span>Exceptional</span>
            </div>
          )}
          {matchScore && (
            <div className={`${getMatchBgColor(matchScore)} px-4 py-2 rounded-full flex items-center gap-2`}>
              <span className={`text-sm font-bold ${getMatchColor(matchScore)}`}>
                {matchScore}% Match
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="p-6 md:p-8 space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
            {roleTitle}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground line-clamp-3">
            {companyDescription}
          </p>
        </div>

        {matchAnalysis && !matchAnalysis?.error && (
          <div className="bg-muted/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Icon name="Target" size={18} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">AI Match Analysis</h3>
            </div>
            <p className="text-sm text-muted-foreground">{matchAnalysis?.matchSummary}</p>
            {matchAnalysis?.keyStrengths?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-foreground mb-1">Key Strengths:</p>
                <ul className="space-y-1">
                  {matchAnalysis?.keyStrengths?.slice(0, 3)?.map((strength, index) => (
                    <li key={index} className="flex items-start gap-1.5">
                      <Icon name="CheckCircle2" size={14} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-foreground">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{location}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
            <Icon name="Briefcase" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{workMode}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{teamSize}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
            <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{growthStage}</span>
          </div>
          {employeeCount && (
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
              <Icon name="Building2" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{employeeCount}</span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Compensation</h3>
            <p className="text-xl md:text-2xl font-bold text-primary">{compensationRange}</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Key Benefits</h3>
            <div className="flex flex-wrap gap-2">
              {benefits?.map((benefit, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-success/10 text-success rounded-lg border border-success/20 text-sm font-medium"
                >
                  {benefit}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Requirements</h3>
            <ul className="space-y-2">
              {requirements?.map((requirement, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-base text-foreground">{requirement}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => onSwipe(opportunity?.id, 'pass')}
              disabled={isAnalyzing}
              className="flex-1 px-6 py-3 bg-white hover:bg-gray-100 text-gray-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
            >
              <Icon name="X" size={20} />
              Pass
            </button>
            <button
              onClick={() => onSwipe(opportunity?.id, 'interest')}
              disabled={isAnalyzing}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Heart" size={20} />
              Interested
            </button>
          </div>

          {isAnalyzing && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-gray-600">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                Analyzing opportunity...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpportunityCard;